import React, { useRef, useState, useEffect } from "react";
import "./Auth.css";
import { useNavigate } from "react-router-dom";
import { commonStyles } from "../../utils/styles/CommonStyle";
import { Theme } from "../../theme/Theme";
import CommonButton from "../../components/common/CommonButton";
import colors from "../../theme/colors";
import { useDispatch, useSelector } from "react-redux";
import { OtpVerificationActioninitiate } from "../../redux/actions/auth/otpVerificationAction";
import { SendOtpActionInitiate } from "../../redux/actions/auth/sendOtpAction";
import { Loader } from "../../utils";
import AuthLayout from "../../components/common/AuthLayout";

const OTP_LENGTH = 6;
const RESEND_INTERVAL = 60; // seconds

const VerifyOtp = () => {
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(RESEND_INTERVAL);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendLimitExceeded, setResendLimitExceeded] = useState(false);
  const [resendsRemaining, setResendsRemaining] = useState(null);
  const inputs = useRef([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.otpVerification);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer(t => t - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/, "");
    if (!val) {
      let newOtp = [...otp];
      newOtp[idx] = "";
      setOtp(newOtp);
      return;
    }
    let newOtp = [...otp];
    newOtp[idx] = val.slice(-1);
    setOtp(newOtp);
    setError("");
    if (idx < OTP_LENGTH - 1) {
      inputs.current[idx + 1].focus();
    }
  };

  const handleBackspace = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      let newOtp = [...otp];
      newOtp[idx - 1] = "";
      setOtp(newOtp);
      inputs.current[idx - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("Text").replace(/\D/g, "");
    if (pasted.length === OTP_LENGTH) {
      setOtp(pasted.split(""));
      inputs.current[OTP_LENGTH - 1].focus();
      setError("");
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();

    if (otp.some(d => d === "") || otp.join("").length !== OTP_LENGTH) {
      setError("Please enter the 6-digit code.");
      return;
    }

    const phoneNumber = localStorage.getItem("auth_phone");
    if (!phoneNumber) {
      setError("Phone number session lost. Please go back and log in again.");
      return;
    }

    setError(""); // Clear any previous errors
    const result = await dispatch(
      OtpVerificationActioninitiate({
        phone_number: phoneNumber,
        otp: otp.join(""),
      })
    );

    if (result?.success) {
      const authFlow = localStorage.getItem("auth_flow");
      localStorage.removeItem("auth_phone");

      if (authFlow === "forgot_password") {
        localStorage.removeItem("auth_flow");
        navigate("/set-password");
      } else {
        const accountType = result.data?.user?.account_type || localStorage.getItem("userType");
        if (accountType === "business") {
          navigate("/onboarding", { state: { freshEntry: true } });
        } else {
          navigate("/dashboard");
        }
      }
    } else {
      // Handle OTP verification failure - show error and allow retry
      setError("Invalid OTP. Please check and try again.");
      setOtp(Array(OTP_LENGTH).fill("")); // Clear the OTP inputs
      inputs.current[0]?.focus(); // Focus on first input for easy retry
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0 || resendLoading || resendLimitExceeded) return;

    const phoneNumber = localStorage.getItem("auth_phone");
    const authFlow = localStorage.getItem("auth_flow");
    if (!phoneNumber) return;

    setResendLoading(true);
    const payload = {
      phone_number: phoneNumber,
      is_forgotpassword: authFlow === "forgot_password"
    };
    const result = await dispatch(SendOtpActionInitiate(payload));
    setResendLoading(false);

    if (result?.rateLimitExceeded) {
      setResendLimitExceeded(true);
      return;
    }

    if (result?.success) {
      setOtp(Array(OTP_LENGTH).fill(""));
      setError("");
      setResendTimer(RESEND_INTERVAL);
      inputs.current[0]?.focus();

      const remaining = result.resends_remaining;
      if (typeof remaining === "number") {
        if (remaining === 0) {
          setResendLimitExceeded(true);
        } else {
          setResendsRemaining(remaining);
        }
      }
    }
  };

  return (
    <AuthLayout
      title="Verify OTP"
      subtitle="Enter the 6-digit code sent to your phone"
      showTabs={false}
      showLegalLinks={false}
    >
      <form className="login-form w-100" style={{ alignItems: "center" }} onSubmit={handleSubmit} autoComplete="off">
        <div
          style={{
            display: "flex", gap: 14, marginTop: 4, justifyContent: "center"
          }}
          onPaste={handlePaste}
        >
          {otp.map((value, idx) => (
            <input
              key={idx}
              type="text"
              inputMode="numeric"
              maxLength={1}
              ref={el => inputs.current[idx] = el}
              className="input otp-input"
              style={{
                ...Theme.font14Medium, width: 40, height: 40, textAlign: "center",
                borderRadius: 12, border: commonStyles.commonBorder, background: "var(--bg-surface)",
                color: colors.mainTextColor, padding: 0, margin: 0, boxShadow: "none"
              }}
              value={value}
              onChange={e => handleChange(e, idx)}
              onKeyDown={e => handleBackspace(e, idx)}
              autoFocus={idx === 0}
            />
          ))}
        </div>
        {error && <div style={Theme.errorText}>{error}</div>}

        <div className="d-flex justify-content-center mx-auto mb-2 w-100 mt-4">
          <CommonButton
            title={
              loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Loader variant="dots" size="small" color="white" />
                  <span>Verifying...</span>
                </div>
              ) : "Verify & Continue"
            }
            type="submit"
            onClick={handleSubmit}
            width={"100%"}
            disabled={loading}
          />
        </div>
      </form>

      <div style={{ width: "100%", textAlign: "center", marginTop: 48 }}>
        <span style={{ color: "var(--text-subtle)", fontSize: 15, marginBottom: 8, display: "block" }}>
          Didn&apos;t receive the code?
        </span>
        <div className="d-flex justify-content-center mx-auto mb-2">
          <CommonButton
            title={
              resendLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Loader variant="dots" size="small" color="primary" />
                  <span>Sending...</span>
                </div>
              ) : resendLimitExceeded
                ? "Resend OTP"
                : resendTimer > 0
                  ? `Resend in ${resendTimer}s`
                  : "Resend OTP"
            }
            onClick={handleResend}
            width={"100%"}
            variant="outline"
            disabled={resendTimer > 0 || resendLoading || resendLimitExceeded}
          />
        </div>
        {resendLimitExceeded && (
          <p style={{ color: "var(--color-error, #d32f2f)", fontSize: 13, marginTop: 8 }}>
            You have reached the maximum resend limit. Please try again after 24 hours.
          </p>
        )}
        {!resendLimitExceeded && resendsRemaining !== null && resendsRemaining > 0 && (
          <p style={{ color: "var(--text-subtle)", fontSize: 13, marginTop: 8 }}>
            {resendsRemaining} resend{resendsRemaining !== 1 ? "s" : ""} remaining today
          </p>
        )}
      </div>

      <style>
        {`
        .otp-input::-webkit-input-placeholder { color: var(--text-muted); }
        .otp-input::placeholder { color: var(--text-muted); }
        @media (max-width: 480px) {
          .otp-input {
            width: 38px !important;
            height: 38px !important;
            font-size: 17px !important;
          }
        }
        `}
      </style>
    </AuthLayout>
  );
};

export default VerifyOtp;
